import React, { useState, useEffect, useRef } from 'react';
import {
    ArrowRight,
    GraduationCap,
    Users,
    Brain,
    BookOpen,
    Presentation,
    CheckCircle2,
    Quote,
    Building2,
    Briefcase,
    Clock,
    ArrowUpRight,
    CheckCircle,
    X
} from 'lucide-react';
import { usePageContext } from '../contexts/PageContext';
import wobLogo from '../logos/wob.svg';

/* ─── Scroll-reveal Animation Utility ─── */
const useIntersectionObserver = (options = {}) => {
    const elementRef = useRef(null);
    const [isVisible, setIsVisible] = useState(false);
    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) { setIsVisible(true); observer.disconnect(); }
        }, { threshold: 0.1, ...options });
        if (elementRef.current) observer.observe(elementRef.current);
        return () => observer.disconnect();
    }, [options]);
    return [elementRef, isVisible];
};

const FadeIn = ({ children, delay = 0, className = "", direction = "up" }) => {
    const [ref, isVisible] = useIntersectionObserver();

    let transformClass = "translate-y-6";
    if (direction === "left") transformClass = "-translate-x-6";
    if (direction === "right") transformClass = "translate-x-6";

    return (
        <div
            ref={ref}
            style={{ transitionDelay: `${delay}ms` }}
            className={`transition-all duration-700 ease-out transform ${isVisible ? 'opacity-100 translate-y-0 translate-x-0' : `opacity-0 ${transformClass}`} ${className}`}
        >
            {children}
        </div>
    );
};

/* ═══════════════════════════════════════════
   Main Page Component
   ═══════════════════════════════════════════ */
export const AITrainingPage = () => {
    const { navigateTo } = usePageContext();

    const openEmail = (subject = "AI Training Booking") => {
        const body = "Hi,\n\nI'd like to book a training session or workshop for my organization.\n\nThank you";
        const mailto = `mailto:hello@panometra.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        window.open(mailto, '_blank');
    };

    const [selectedProgram, setSelectedProgram] = useState(null);

    const programs = [
        {
            title: "For Educators",
            color: "bg-[#f9a8d4]",
            textColor: "text-rose-900",
            subject: "Educator",
            description: "Empower your teaching with AI. Learn to automate grading, create dynamic lesson plans, and personalize student learning experiences.",
            features: ["Automated Lesson Planning", "Smart Grading Workflows", "Student Engagement AI"]
        },
        {
            title: "For Executives",
            color: "bg-[#bfdbfe]",
            textColor: "text-blue-900",
            subject: "Executive",
            description: "Lead your organization into the AI era. Discover strategic frameworks and risk management for implementing enterprise AI.",
            features: ["Strategic AI Deployment", "Risk Management Frameworks", "ROI Analysis for AI"]
        },
        {
            title: "For Government",
            color: "bg-[#a78bfa]",
            textColor: "text-purple-900",
            subject: "Government",
            description: "Modernize public service with secure AI. Learn compliance, policy-making with AI, and improving citizen services efficiently.",
            features: ["Compliant AI Infrastructure", "Data Privacy Frameworks", "Citizen Service Automation"]
        }
    ];

    /* ─── Testimonials Data ─── */
    const testimonials = [
        {
            quote: "This was the first time AI actually made sense to me. Panometra showed us how to use AI tools in our daily work. It completely changed how our team approaches tasks.",
            author: "Marcus Alabi",
            role: "Program Manager — NGO",
            avatar: "https://images.unsplash.com/photo-1614890085618-0e1054da74f8?q=80&w=250&auto=format&fit=crop"
        },
        {
            quote: "The training was simple, practical, and very engaging. I now use AI every day for research and writing. It feels like having a personal learning coach.",
            author: "Taiye Ajayi",
            role: "Student — University of Lagos",
            avatar: "https://images.unsplash.com/photo-1629747387925-6905ff5a558a?q=80&w=250&auto=format&fit=crop"
        },
        {
            quote: "Our organization now saves hours every week. The AI workflow techniques we learned were immediately useful. Project integration has become effortless.",
            author: "David Oka",
            role: "Operations Director — Tech Forward",
            avatar: "https://images.unsplash.com/photo-1628149321186-7d4c5b1170f9?q=80&w=250&auto=format&fit=crop"
        }
    ];

    return (
        <div className="font-sans antialiased text-zinc-900 overflow-hidden bg-[#faf9f6]">

            {/* ═══ HEADER ═══ */}
            <nav className="fixed w-full z-40 bg-[#faf9f6]/90 backdrop-blur-md border-b border-zinc-200">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-[72px] flex items-center justify-between">
                    <div onClick={() => navigateTo('landing')} className="flex items-center cursor-pointer group">
                        <img src={wobLogo} alt="Panometra" className="h-5 sm:h-7 w-auto group-hover:opacity-70 transition-opacity duration-300 invert" />
                    </div>
                    <button
                        onClick={() => openEmail()}
                        className="group inline-flex items-center gap-2 px-5 py-2 bg-zinc-900 text-white text-xs font-semibold uppercase tracking-wider rounded-full hover:bg-zinc-800 transition-all duration-200"
                    >
                        Book Training
                        <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
                    </button>
                </div>
            </nav>

            {/* ═══ HERO ═══ */}
            <section className="pt-36 pb-20 lg:pt-44 lg:pb-32 relative">
                <div className="max-w-[1240px] mx-auto px-5 sm:px-8">
                    <div className="max-w-3xl mb-16 relative z-10">
                        <FadeIn delay={100}>
                            <h1 className="text-5xl sm:text-6xl lg:text-[4.5rem] font-bold tracking-tight leading-[1.05] text-zinc-900 mb-8">
                                Learn AI. <br className="hidden sm:block" />Apply AI. Lead with AI.
                            </h1>
                        </FadeIn>
                        <FadeIn delay={180}>
                            <p className="text-xl sm:text-2xl text-zinc-600 leading-relaxed max-w-2xl mb-10">
                                Panometra helps individuals and organizations move from AI beginners to confident AI users through practical, live training.
                            </p>
                        </FadeIn>
                        <FadeIn delay={250}>
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 mb-8 text-sm font-semibold text-zinc-500 uppercase tracking-widest">
                                <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-zinc-900" /> Live Training</span>
                                <span className="hidden sm:inline">•</span>
                                <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-zinc-900" /> Virtual Workshops</span>
                                <span className="hidden sm:inline">•</span>
                                <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-zinc-900" /> Hands-on Learning</span>
                            </div>
                        </FadeIn>
                        <FadeIn delay={300}>
                            <button
                                onClick={() => openEmail()}
                                className="group inline-flex items-center gap-3 px-8 py-4 bg-zinc-900 text-white rounded-full font-bold text-sm hover:bg-zinc-800 transition-all duration-300 shadow-xl shadow-zinc-900/10 hover:shadow-zinc-900/20 hover:-translate-y-0.5"
                            >
                                Book a Training Session
                                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                            </button>
                        </FadeIn>
                    </div>

                    {/* Aesthetic Bento Image Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 mt-12 relative">
                        <FadeIn direction="right" delay={400} className="md:col-span-4 h-64 md:h-[400px] rounded-3xl overflow-hidden relative group">
                            <img src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?q=80&w=2071&auto=format&fit=crop" alt="Students learning" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-6">
                                <span className="text-white font-semibold flex items-center gap-2 bg-white/20 backdrop-blur-md px-4 py-2 rounded-full text-sm">
                                    <Users size={16} /> Group Sessions
                                </span>
                            </div>
                        </FadeIn>
                        <FadeIn direction="left" delay={500} className="md:col-span-8 h-64 md:h-[400px] rounded-3xl overflow-hidden relative group">
                            <img src="https://images.unsplash.com/photo-1573164574572-cb89e39749b4?q=80&w=2069&auto=format&fit=crop" alt="Professional using AI" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-6">
                                <span className="text-white font-semibold flex items-center gap-2 bg-white/20 backdrop-blur-md px-4 py-2 rounded-full text-sm">
                                    <Brain size={16} /> Hands-on Workflows
                                </span>
                            </div>
                        </FadeIn>
                    </div>
                </div>
            </section>

            {/* ═══ PROBLEM ═══ */}
            <section className="py-24 lg:py-32 bg-white">
                <div className="max-w-[1240px] mx-auto px-5 sm:px-8">
                    <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
                        <div className="lg:col-span-5 relative z-10">
                            <FadeIn>
                                <p className="text-xs font-bold tracking-widest uppercase text-rose-500 mb-6">The Challenge</p>
                                <h2 className="text-4xl lg:text-[2.75rem] font-bold tracking-tight mb-6 leading-[1.15] text-zinc-900">
                                    AI is changing everything — but most people are being left behind.
                                </h2>
                                <p className="text-base sm:text-lg text-zinc-500 leading-relaxed">
                                    Students, teachers, nonprofits, and professionals often don't have practical training to help them use AI confidently. The result? People hear about AI every day — but don't know how to actually use it.
                                </p>
                            </FadeIn>
                        </div>

                        <div className="lg:col-span-7 space-y-4">
                            {[
                                "Not knowing where to start with AI",
                                "Feeling overwhelmed by new tools",
                                "Not understanding how AI applies to their work",
                                "Missing opportunities to become more productive"
                            ].map((text, i) => (
                                <FadeIn key={i} delay={i * 100} direction="left">
                                    <div className="px-6 py-6 sm:px-8 sm:py-7 rounded-[1.25rem] bg-[#fcfcfc] border border-zinc-100/50 flex items-center gap-6 group hover:bg-[#faf9f6] transition-colors duration-300">
                                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm border border-zinc-100">
                                            <Clock size={16} strokeWidth={2.5} className="text-rose-500" />
                                        </div>
                                        <p className="font-bold text-zinc-900 text-[1.1rem] sm:text-lg">{text}</p>
                                    </div>
                                </FadeIn>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ═══ SOLUTION ═══ */}
            <section className="py-24 lg:py-32 bg-[#faf9f6]">
                <div className="max-w-[1240px] mx-auto px-5 sm:px-8">
                    <div className="mb-12 lg:mb-16">
                        <FadeIn>
                            <p className="text-[13px] font-semibold tracking-wide text-zinc-500 mb-6 drop-shadow-none uppercase">The Solution</p>
                            <h2 className="text-[2.75rem] sm:text-[3.5rem] lg:text-[4.5rem] font-bold tracking-tight leading-[1.05] text-[#111] max-w-4xl">
                                Practical AI training for everyday productivity
                            </h2>
                        </FadeIn>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                        {/* Left Side: Image with Floating Badge */}
                        <FadeIn direction="right" delay={100} className="relative h-full min-h-[400px] lg:min-h-[500px] rounded-3xl overflow-hidden shadow-sm border border-zinc-100/50">
                            <img
                                src="https://images.unsplash.com/photo-1552581234-26160f608093?q=80&w=2070&auto=format&fit=crop"
                                alt="Training session"
                                className="absolute inset-0 w-full h-full object-cover"
                            />

                            {/* Floating Badge */}
                            <div className="absolute bottom-6 left-6 sm:bottom-8 sm:left-8 bg-white/95 backdrop-blur-md p-2 rounded-full flex items-center gap-4 shadow-xl border border-white/20">
                                <div className="flex items-center -space-x-2 pl-2">
                                    <div className="w-8 h-8 rounded-full border-2 border-white bg-blue-100 flex items-center justify-center font-bold text-xs text-blue-700">T</div>
                                    <div className="w-8 h-8 rounded-full border-2 border-white bg-rose-100 flex items-center justify-center font-bold text-xs text-rose-700">J</div>
                                    <div className="w-8 h-8 rounded-full border-2 border-white bg-green-100 flex items-center justify-center font-bold text-xs text-green-700">S</div>
                                </div>
                                <div className="pr-5 py-1">
                                    <p className="text-[11px] font-bold text-[#111] leading-tight">Join 100+ forward-</p>
                                    <p className="text-[11px] font-medium text-zinc-500 leading-tight">thinking professionals</p>
                                </div>
                            </div>
                        </FadeIn>

                        {/* Right Side: Content */}
                        <div className="flex flex-col justify-center py-4 lg:py-6 lg:pl-4">
                            <FadeIn delay={200} direction="left" className="mb-10">
                                <p className="text-[1.1rem] lg:text-[1.1rem] font-medium text-[#111] leading-relaxed max-w-lg">
                                    Empowering individuals and teams through a practical approach that makes learning AI intuitive and immediately useful.
                                </p>
                            </FadeIn>

                            <div className="space-y-6 lg:space-y-8">
                                {/* Item 1 */}
                                <FadeIn delay={300} direction="left" className="flex items-start gap-6 sm:gap-8 hover:bg-white/50 p-4 -ml-4 rounded-2xl transition-colors">
                                    <div className="flex-shrink-0 w-12 h-12 rounded-[14px] bg-blue-500 flex items-center justify-center text-white shadow-md shadow-blue-500/20">
                                        <Briefcase size={22} strokeWidth={2} />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-[#111] mb-2 sm:mb-1">Work Faster</h3>
                                        <p className="text-[15px] text-zinc-500 leading-relaxed">
                                            Streamline your daily operations and enhance efficiency by learning to automate everyday tasks with AI.
                                        </p>
                                    </div>
                                </FadeIn>

                                {/* Item 2 */}
                                <FadeIn delay={400} direction="left" className="flex items-start gap-6 sm:gap-8 hover:bg-white/50 p-4 -ml-4 rounded-2xl transition-colors">
                                    <div className="flex-shrink-0 w-12 h-12 rounded-[14px] bg-[#f97316] flex items-center justify-center text-white shadow-md shadow-orange-500/20">
                                        <Presentation size={22} strokeWidth={2} />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-[#111] mb-2 sm:mb-1">Communicate Better</h3>
                                        <p className="text-[15px] text-zinc-500 leading-relaxed">
                                            Save time on emails, reports, and presentations using guided AI workflows for immediate results.
                                        </p>
                                    </div>
                                </FadeIn>

                                {/* Item 3 */}
                                <FadeIn delay={500} direction="left" className="flex items-start gap-6 sm:gap-8 hover:bg-white/50 p-4 -ml-4 rounded-2xl transition-colors">
                                    <div className="flex-shrink-0 w-12 h-12 rounded-[14px] bg-[#ec4899] flex items-center justify-center text-white shadow-md shadow-pink-500/20">
                                        <Brain size={22} strokeWidth={2} />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-[#111] mb-2 sm:mb-1">Make Smarter Decisions</h3>
                                        <p className="text-[15px] text-zinc-500 leading-relaxed">
                                            Analyze information and solve problems effectively without a technical background.
                                        </p>
                                    </div>
                                </FadeIn>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ═══ OFFERINGS ═══ */}
            <section className="py-24 lg:py-32 bg-[#faf9f6]">
                <div className="max-w-[1240px] mx-auto px-5 sm:px-8">
                    <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-stretch">

                        {/* Left Side: Stacked Cards */}
                        <div className="lg:col-span-5 flex flex-col justify-center">
                            <FadeIn className="mb-8">
                                <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight mb-4 leading-tight text-[#111]">
                                    Our Transformative<br />AI Programs.
                                </h2>
                                <p className="text-lg text-zinc-500 max-w-md">
                                    We provide live and virtual training sessions tailored to different groups.
                                </p>
                            </FadeIn>

                            <div className="relative h-[350px] sm:h-[450px] lg:h-[500px] mt-8">
                                {programs.map((program, idx) => {
                                    // Static positioning matching the resting visual stack from the screenshot
                                    // Lowest idx is Pink (top vertically, but furthest back essentially)
                                    let zIndex = 20 + idx * 10;
                                    const topOffset = idx * 105; // Cards are shifted down over each other by a larger amount

                                    return (
                                        <div
                                            key={idx}
                                            className="absolute left-0 w-full hover:-translate-y-2 transition-transform duration-300"
                                            style={{ top: `${topOffset}px`, zIndex }}
                                        >
                                            <button
                                                onClick={() => setSelectedProgram(program)}
                                                className={`w-full text-left p-6 sm:p-8 rounded-[2rem] ${program.color} text-white relative overflow-hidden shadow-lg hover:shadow-xl transition-shadow group ${idx === 2 ? 'min-h-[250px]' : 'min-h-[170px]'}`}
                                            >
                                                {/* Decorative pattern for Government Card */}
                                                {idx === 2 && (
                                                    <div className="absolute -bottom-8 -right-8 opacity-20 w-80 h-80 pointer-events-none">
                                                        <svg viewBox="0 0 100 100" className="w-full h-full text-white" fill="currentColor">
                                                            <pattern id="p" width="25" height="25" patternUnits="userSpaceOnUse"><path d="M0 25a25 25 0 0 1 25-25v25H0z" /></pattern>
                                                            <rect width="100" height="100" fill="url(#p)" />
                                                        </svg>
                                                    </div>
                                                )}

                                                <div className="relative z-10 flex justify-between items-start pt-1">
                                                    <span className="text-xl sm:text-2xl font-semibold tracking-wide text-white/95">{program.title}</span>
                                                    <ArrowRight className="opacity-90 group-hover:translate-x-1 transition-all" strokeWidth={1.5} size={24} />
                                                </div>
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Right Side: Image Grid/Collage */}
                        <FadeIn direction="left" delay={200} className="lg:col-span-7 relative min-h-[400px] lg:min-h-[500px] rounded-[2rem] bg-zinc-100 overflow-hidden shadow-sm border border-zinc-200/50 flex">
                            {/* Main Subject Image */}
                            <img
                                src="https://images.unsplash.com/photo-1580411402629-e0cdf76f3d3b?q=80&w=1287&auto=format&fit=crop"
                                alt="Educator training session"
                                className="w-full h-full object-cover object-center"
                            />

                            {/* Floating UI Elements matching reference */}
                            <div className="absolute top-8 left-8 bg-white/95 backdrop-blur rounded-2xl p-4 shadow-xl border border-white/20 max-w-[240px] hidden sm:block">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-8 h-8 rounded-lg bg-purple-500 flex items-center justify-center text-white"><Brain size={16} /></div>
                                    <span className="font-bold text-sm text-[#111]">AI Tutor</span>
                                </div>
                                <div className="bg-[#fcf8f3] text-zinc-700 text-xs p-3 rounded-b-xl rounded-tr-xl mb-2">
                                    How can I help you with your lesson today?
                                </div>
                                <div className="bg-[#1a1a2e] text-white text-xs p-3 rounded-t-xl rounded-bl-xl ml-6">
                                    I need help with algebra...
                                </div>
                            </div>

                            <div className="absolute bottom-8 right-8 bg-white/95 backdrop-blur rounded-2xl p-4 shadow-xl border border-white/20 w-[260px] hidden sm:block">
                                <p className="font-bold text-sm text-[#111] mb-2">Top Performer</p>
                                <div className="flex items-center gap-3 mb-3 p-2 bg-white rounded-xl border border-zinc-100 shadow-sm">
                                    <div className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center text-white"><BookOpen size={20} /></div>
                                    <div>
                                        <p className="font-bold text-sm leading-tight text-[#111]">First Place</p>
                                        <p className="text-[11px] text-zinc-500 leading-tight">Prompt Challenge</p>
                                    </div>
                                </div>
                                <div className="bg-[#1a1a2e] p-3 rounded-xl flex flex-col items-center">
                                    <p className="text-[11px] text-zinc-400 mb-2">Recent Modules</p>
                                    <div className="flex items-center justify-between w-full">
                                        <div className="w-6 h-6 rounded-full border-2 border-white bg-orange-100"></div>
                                        <div className="w-6 h-6 rounded-full border-2 border-white bg-blue-100"></div>
                                        <div className="w-6 h-6 rounded-full border-2 border-white bg-pink-100"></div>
                                        <div className="w-6 h-6 rounded-full border-2 border-white bg-green-100"></div>
                                        <div className="w-6 h-6 rounded-full border-2 border-white bg-purple-100"></div>
                                    </div>
                                </div>
                            </div>
                        </FadeIn>

                    </div>
                </div>
            </section>

            {/* ═══ TESTIMONIALS ═══ */}
            <section className="py-24 lg:py-32 bg-[#faf9f6]">
                <div className="max-w-[1300px] mx-auto px-5 sm:px-8">
                    {/* Header Area */}
                    <FadeIn>
                        <div className="flex flex-col mb-16 relative">
                            {/* Top right floating text */}
                            <div className="w-full flex justify-end mb-4 sm:mb-8">
                                <span className="text-zinc-500 font-medium text-[1.1rem]">Testimonial</span>
                            </div>

                            <div className="flex flex-col lg:flex-row lg:items-end justify-between w-full">
                                {/* Navigation Buttons */}
                                <div className="flex items-center gap-3 order-2 lg:order-1 mt-10 lg:mt-0 lg:mb-2">
                                    <button className="w-12 h-12 rounded-xl bg-[#e5e5e5] text-zinc-400 flex items-center justify-center hover:bg-zinc-300 transition-colors">
                                        <ArrowRight className="rotate-180" size={20} />
                                    </button>
                                    <button className="w-12 h-12 rounded-xl bg-black text-white flex items-center justify-center hover:bg-zinc-800 transition-colors shadow-lg">
                                        <ArrowRight size={20} />
                                    </button>
                                </div>

                                {/* Title */}
                                <h2 className="text-[2.75rem] sm:text-5xl lg:text-[5.5rem] font-medium tracking-tight text-[#111] lg:text-right order-1 lg:order-2 leading-[1.05] max-w-4xl">
                                    What our participants<br className="hidden sm:block" /> say about Training
                                </h2>
                            </div>
                        </div>
                    </FadeIn>

                    <div className="grid md:grid-cols-3 gap-6">
                        {testimonials.map((test, i) => (
                            <FadeIn key={i} delay={i * 150}>
                                <div className="p-8 sm:p-10 rounded-[2rem] bg-white h-full flex flex-col justify-between overflow-hidden shadow-sm">
                                    <div>
                                        <div className="text-zinc-400 font-medium mb-12 flex items-center gap-2 text-[1.05rem]">
                                            <span className="w-5 h-[2px] bg-zinc-300 rounded-full"></span> 0{i + 1}
                                        </div>
                                        <p className="text-[1.15rem] text-[#111] font-medium leading-relaxed mb-12">
                                            “{test.quote}”
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-xl overflow-hidden shrink-0 border border-zinc-100 bg-zinc-100">
                                            <img src={test.avatar} alt={test.author} className="w-full h-full object-cover" />
                                        </div>
                                        <div className="flex flex-col">
                                            <p className="font-semibold text-[#111] text-sm">{test.author}</p>
                                            <p className="text-[13px] text-zinc-500 mt-0.5">{test.role}</p>
                                        </div>
                                    </div>
                                </div>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            {/* ═══ CTA ═══ */}
            <section className="py-24 lg:py-32 bg-zinc-900 text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1543269865-cbf427effbad?q=80&w=2070&auto=format&fit=crop')] opacity-10 bg-cover bg-center mix-blend-overlay"></div>
                <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center relative z-10">
                    <FadeIn>
                        <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-8">
                            Start your AI journey today.
                        </h2>
                        <p className="text-xl text-zinc-400 mb-12 max-w-2xl mx-auto leading-relaxed">
                            AI is quickly becoming an essential skill. Whether you're an individual or an organization, our training will help you unlock the real value of AI.
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <button
                                onClick={() => openEmail("Booking: AI Training Session")}
                                className="w-full sm:w-auto px-8 py-4 bg-white text-zinc-900 rounded-full font-bold text-sm hover:bg-zinc-100 transition-all duration-300 hover:scale-105 active:scale-95"
                            >
                                Book a Training Session
                            </button>
                            <button
                                onClick={() => openEmail("Request: AI Workshop for Organization")}
                                className="w-full sm:w-auto px-8 py-4 bg-zinc-800 text-white rounded-full font-bold text-sm hover:bg-zinc-700 transition-all duration-300 border border-zinc-700 hover:border-zinc-600"
                            >
                                Request a Workshop
                            </button>
                        </div>
                        <p className="mt-8 text-sm text-zinc-500 font-medium uppercase tracking-widest">
                            Let's make AI simple and useful — together.
                        </p>
                    </FadeIn>
                </div>
            </section>

            {/* ═══ PROGRAM MODAL ═══ */}
            {selectedProgram && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 animate-in fade-in duration-200">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-zinc-900/60 backdrop-blur-sm"
                        onClick={() => setSelectedProgram(null)}
                    ></div>

                    {/* Modal Content */}
                    <div className="relative w-full max-w-lg bg-white rounded-[2rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
                        {/* Header Area */}
                        <div className={`pt-10 pb-6 px-8 ${selectedProgram.color} text-white relative`}>
                            {/* Decorative element */}
                            <div className="absolute -top-12 -right-12 opacity-20 w-48 h-48 pointer-events-none mix-blend-overlay">
                                <svg viewBox="0 0 100 100" className="w-full h-full text-white" fill="currentColor">
                                    <pattern id="modal-p" width="25" height="25" patternUnits="userSpaceOnUse"><path d="M0 25a25 25 0 0 1 25-25v25H0z" /></pattern>
                                    <rect width="100" height="100" fill="url(#modal-p)" />
                                </svg>
                            </div>

                            <button
                                onClick={() => setSelectedProgram(null)}
                                className="absolute top-6 right-6 p-2 bg-black/10 hover:bg-black/20 rounded-full text-white transition-colors"
                            >
                                <X size={20} />
                            </button>
                            <h3 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2 relative z-10">{selectedProgram.title}</h3>
                            <p className="text-white/90 font-medium relative z-10 flex items-center gap-2">
                                <Clock size={16} /> Live & Virtual Training
                            </p>
                        </div>

                        {/* Body Area */}
                        <div className="p-8">
                            <p className="text-zinc-600 text-[1.05rem] leading-relaxed mb-6">
                                {selectedProgram.description}
                            </p>

                            <div className="mb-8 p-6 bg-zinc-50 rounded-2xl border border-zinc-100">
                                <h4 className="font-bold text-zinc-900 mb-4 text-sm uppercase tracking-wider">What you will learn</h4>
                                <ul className="space-y-3">
                                    {selectedProgram.features.map((feature, i) => (
                                        <li key={i} className="flex items-start gap-3">
                                            <CheckCircle2 size={18} className={`mt-0.5 shrink-0 ${selectedProgram.textColor}`} />
                                            <span className="text-zinc-700 text-sm font-medium">{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <button
                                onClick={() => {
                                    openEmail(`Booking: ${selectedProgram.subject} Training`);
                                    setSelectedProgram(null);
                                }}
                                className={`w-full py-4 rounded-xl font-bold text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 ${selectedProgram.color}`}
                            >
                                Book this Program
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
